import { useLayoutEffect, useRef } from 'react';

/**
 * Compute the vertical position that keeps the newest thinking content in view.
 * Exported separately so the pinning rule can be tested without a browser layout.
 */
export function rollingThinkingOffset(viewportHeight: number, contentHeight: number): number {
  return contentHeight > viewportHeight ? viewportHeight - contentHeight : 0;
}

/**
 * Keep a clipped thinking viewport pinned to its newest content with a retargetable CSS transition.
 * Updating the transform (instead of queuing smooth scroll operations) lets a fast stream keep
 * moving naturally: each update retargets the current animation rather than adding another one.
 */
export function useRollingThinkingText(text: string) {
  const viewport = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const viewportNode = viewport.current;
    const trackNode = track.current;
    if (!viewportNode || !trackNode) return;

    const updateOffset = () => {
      trackNode.style.setProperty(
        '--rolling-thinking-offset',
        `${rollingThinkingOffset(viewportNode.clientHeight, trackNode.scrollHeight)}px`,
      );
    };

    updateOffset();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateOffset);
    observer.observe(viewportNode);
    observer.observe(trackNode);
    return () => observer.disconnect();
  }, [text]);

  return { viewport, track };
}
